"use client";

import {
  Globe2,
  Pencil,
  ScrollText,
  Search,
  Users,
  type LucideIcon,
} from "lucide-react";
import { useRouter } from "next/navigation";
import { useMemo, useState, type ReactNode } from "react";

import { Container } from "@/components/layout/container";
import { OverlayPanel, OverlayPanelContent } from "@/components/overlay-panel";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useSearchVisaQuery } from "@/feature/visa/search/api/visa-search-api";
import {
  encodedVisaParams,
  toVisaSearchQueryArgs,
} from "@/feature/visa/search/lib/visa-formatter-helper";
import {
  formatVisaCountryMeta,
  formatVisaTravelerLabel,
} from "@/feature/visa/search/lib/visa-search-display-helpers";
import type { IDecodedVisaSearch } from "@/feature/visa/search/types/visa.types";
import { cn } from "@/lib/utils";
import { baseApi } from "@/redux/api/base-api";
import { useAppDispatch } from "@/redux/hooks";
import { VisaSearchForm } from "./form/visa-search-form";

type VisaSearchRouteInfoProps = {
  searchData: IDecodedVisaSearch;
};

type SummaryItem = {
  key: string;
  icon: LucideIcon;
  value: string;
};

export default function VisaSearchRouteInfo({
  searchData,
}: VisaSearchRouteInfoProps) {
  const router = useRouter();
  const dispatch = useAppDispatch();
  const [showModifyBar, setShowModifyBar] = useState(!searchData.isValid);
  const isValid = searchData.isValid;
  const searchQueryArgs = useMemo(
    () => toVisaSearchQueryArgs(searchData),
    [searchData],
  );
  const { isLoading, isFetching } = useSearchVisaQuery(searchQueryArgs);

  const routeLabel = useMemo(() => {
    if (!searchData.country?.name) {
      return "All countries";
    }

    return searchData.country.iso
      ? `${searchData.country.name} (${searchData.country.iso})`
      : searchData.country.name;
  }, [searchData]);

  const summaryItems = useMemo<SummaryItem[]>(
    () => [
      {
        key: "country-meta",
        icon: Globe2,
        value: formatVisaCountryMeta(searchData),
      },
      {
        key: "visa-type",
        icon: ScrollText,
        value: searchData.visa_type?.name || "All visa types",
      },
      {
        key: "travelers",
        icon: Users,
        value: formatVisaTravelerLabel(searchData),
      },
    ],
    [searchData],
  );

  function handleSearchClick() {
    const params = encodedVisaParams(searchData);
    dispatch(baseApi.util.invalidateTags(["VISA_SEARCH"]));
    router.push(params ? `/visa?${params}` : "/visa", { scroll: false });
  }

  return (
    <>
      <OverlayPanel open={showModifyBar} onOpenChange={setShowModifyBar}>
        <section className="sticky top-(--header-height) z-40 overflow-visible border-b border-border/40 bg-background shadow-md">
          <Container className="py-3">
            <div
              aria-hidden={showModifyBar}
              className={cn(
                "flex w-full flex-col gap-2.5 transition-opacity duration-200 md:flex-row md:items-center md:justify-between",
                showModifyBar && "pointer-events-none invisible opacity-0",
              )}
            >
              <div className="flex w-full min-w-0 items-stretch gap-2 md:w-auto">
                <div className="min-w-0 flex-1 md:flex-none">
                  <RouteCard
                    routeLabel={routeLabel}
                    onClick={() => setShowModifyBar(true)}
                  />
                </div>

                <Button
                  type="button"
                  onClick={handleSearchClick}
                  className="md:hidden"
                >
                  <Search className="h-4 w-4" />
                  Search
                </Button>
              </div>

              <div className="flex w-full min-w-0 sm:gap-2  md:w-auto  md:items-center md:justify-end">
                {summaryItems.map((item) => (
                  <SummaryCard
                    key={item.key}
                    icon={<item.icon className="h-3.5 w-3.5 shrink-0" />}
                    value={item.value}
                    onClick={() => setShowModifyBar(true)}
                    className="flex-1 justify-start sm:flex-none"
                  />
                ))}

                {isValid ? (
                  <Button
                    type="button"
                    onClick={handleSearchClick}
                    className="hidden md:flex"
                  >
                    <Search className="h-4 w-4" />
                    Search
                  </Button>
                ) : (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowModifyBar(true)}
                    className="hidden md:flex"
                  >
                    <Pencil className="h-4 w-4" />
                    Modify
                  </Button>
                )}
              </div>
            </div>
          </Container>

          <OverlayPanelContent
            className={cn(
              "top-0 bg-background shadow-[0_8px_10px_-5px_rgba(0,0,0,0.1)]",
            )}
          >
            <Container className="py-7">
              <VisaSearchForm
                searchData={searchData}
                setOpen={setShowModifyBar}
              />
            </Container>
          </OverlayPanelContent>
          <div className="absolute w-full">
            {(isLoading || isFetching) && (
              <div className="bar w-full">
                <div></div>
              </div>
            )}
          </div>
        </section>
      </OverlayPanel>
    </>
  );
}

function SummaryCard({
  icon,
  value,
  onClick,
  className,
}: {
  icon: ReactNode;
  value: string;
  onClick: () => void;
  className?: string;
}) {
  return (
    <Button
      variant="ghost"
      type="button"
      onClick={onClick}
      className={cn(
        "min-w-0 border border-transparent bg-transparent px-3 hover:border-border/70 hover:bg-muted/70",
        className,
      )}
    >
      <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
        {icon}
      </span>
      <div className="min-w-0 text-left leading-tight">
        <p className="truncate text-sm font-semibold">{value}</p>
      </div>
    </Button>
  );
}

function RouteCard({
  routeLabel,
  onClick,
  className,
}: {
  routeLabel: string;
  onClick: () => void;
  className?: string;
}) {
  return (
    <Button
      variant="ghost"
      onClick={onClick}
      className={cn(
        "h-10 w-full min-w-0 justify-start overflow-hidden rounded-xl border border-transparent bg-transparent px-3 hover:border-border/70 hover:bg-muted/70",
        className,
      )}
    >
      <Badge>Visa</Badge>
      <span className="inline-flex min-w-0 items-center gap-1">
        <Globe2 className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        <span className="truncate text-sm font-medium">{routeLabel}</span>
      </span>
    </Button>
  );
}
